import React, { useEffect, useState, useRef, useCallback } from "react";
import cn from "classnames";
import { FormattedMessage, useIntl } from "react-intl";

import { SelectWrapper, StyledSelect, Option, Wrapper, Options } from "./styles";
import messages from "../messages";
import { IProps } from "./interfaces";
import { IOption } from "../../../../types";

const Caret = () => (
    <div className={"Caret"}>
        <svg width="11" height="6">
            <polygon points="0,0 11,0 5.5,5.5" />
        </svg>
    </div>
);

const InputSelect = <T extends string | number | undefined | null>({
    value,
    onChange,
    disabled,
    name,
    options,
    lightWithValue,
    minPlaceholder,
    maxPlaceholder,
    noDelete,
    dark,
    optionsMinWidth,
    optionsRight,
    validation,
    simulation,
    disableTypeahead,
    showNull,
    forceDirection,
}: IProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const optionsRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const isTypeahead = options.length > 3 && !disableTypeahead;
    const [isFlipped, setIsFlipped] = useState(false);
    const clickedOnOptions = useRef(false);
    const { formatMessage } = useIntl();

    const clickOutside = useCallback(() => {
        if (simulation) return;
        if (clickedOnOptions.current) {
            clickedOnOptions.current = false;
            return;
        }
        setIsOpen(false);
        setQuery("");
        window.removeEventListener("click", clickOutside);
    }, [simulation, setIsOpen, setQuery]);

    const handleOpen = (focus: boolean) => {
        if (simulation) return;
        let { top } = wrapperRef!.current!.getBoundingClientRect();
        let { innerHeight } = window;
        let flipped = false;
        if (top + 21 > innerHeight / 2) {
            flipped = true;
        }
        if (forceDirection) {
            flipped = forceDirection === "up" ? true : false;
        }
        if (disabled) return;
        if (!isOpen || focus) {
            let startTop = 0;
            if (value !== undefined && options.length > 6) {
                let index = options.findIndex((option) => option.value === value);
                startTop = index * 40 - 36 * 2.5;
            }
            setTimeout(() => {
                if (optionsRef!.current!) {
                    optionsRef!.current!.scrollTop = startTop;
                }
                if (!isTypeahead) {
                    window.addEventListener("click", clickOutside);
                }
            });
        } else {
            window.removeEventListener("click", clickOutside);
        }
        setIsFlipped(flipped);
        setIsOpen(() => focus ? true : !isOpen)
    };

    useEffect(() => {
        if (simulation) {
            setIsOpen(true);
        }
        return () => {
            window.removeEventListener("click", clickOutside);
        }
    }, []);

    const onOptionsMouseDown = () => {
        if (isTypeahead) {
            clickedOnOptions.current = true;
        }
    };
    const getTypeaheadPlaceholder = () => {
        if (value === undefined || (value === null && !showNull) || value === "") {
            return formatMessage(messages.typeahead);
        } else {
            const selectedOption = options.find((option) => option.value === value);
            if (!selectedOption) {
                return formatMessage(messages.typeahead);
            }
            return selectedOption.label as string;
        }
    };
    const handleQueryChange = ({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
        setQuery(value);
    };
    const getOptions = () => {
        if (!isTypeahead || !query) {
            return options;
        } else {
            return options.filter(({ label }) => {
                if (label && typeof label === "string" && label.toLowerCase().indexOf(query.toLowerCase()) !== -1) {
                    return true;
                }
                return false;
            });
        }
    };
    let hasValue = value !== undefined && options[options.findIndex((option) => option.value === value)];
    const queriedOptions = getOptions();
    return (
        <Wrapper ref={wrapperRef} style={{ marginBottom: simulation ? 270 : 0 }}>
            {!isTypeahead && (
                <SelectWrapper
                    className={cn(
                        disabled && "SelectDisabled",
                        isOpen && "SelectOpened",
                        lightWithValue && value !== undefined && "SelectLight",
                        validation && "ValidationState",
                        dark && "Dark"
                    )}
                    tabIndex={!disabled ? 0 : undefined}
                >
                    <StyledSelect className="Select" onClick={() => handleOpen(false)}>
                        {hasValue ? (
                            options[options.findIndex((option) => option.value === value)].label
                        ) : minPlaceholder ? (
                            "min"
                        ) : maxPlaceholder ? (
                            "max"
                        ) : (
                            <FormattedMessage {...messages.choose} />
                        )}
                        <Caret />
                    </StyledSelect>
                </SelectWrapper>
            )}
            {isTypeahead && (
                <SelectWrapper
                    className={cn(
                        disabled && "SelectDisabled",
                        isOpen && "SelectOpened",
                        lightWithValue && value !== undefined && "SelectLight",
                        validation && "ValidationState",
                        dark && "Dark"
                    )}
                >
                    <StyledSelect
                        as="input"
                        disabled={disabled}
                        onFocus={() => handleOpen(true)}
                        onBlur={clickOutside}
                        ref={inputRef}
                        placeholder={getTypeaheadPlaceholder()}
                        value={query}
                        onChange={handleQueryChange}
                        style={{
                            cursor: "auto",
                        }}
                    />
                    <Caret />
                </SelectWrapper>
            )}
            {isOpen && (
                <Options
                    className={cn("Options", isFlipped && "Flipped")}
                    style={{ minWidth: optionsMinWidth, right: optionsRight }}
                    onMouseDown={onOptionsMouseDown}
                >
                    <div className={"OptionsScroll"} ref={optionsRef}>
                        {hasValue && !noDelete && (
                            <Option
                                onClick={() => {
                                    if (value === undefined && !simulation) return setIsOpen(false);
                                    if (isTypeahead && !simulation) {
                                        setIsOpen(false);
                                        setQuery("");
                                        inputRef!.current!.blur();
                                    }
                                    onChange({
                                        target: {
                                            name,
                                            value: undefined,
                                            type: "select",
                                        },
                                    });
                                }}
                            >
                                <FormattedMessage {...messages.delete} />
                            </Option>
                        )}
                        {queriedOptions.map((option) => {
                            return (
                                <Option
                                    selected={value === option.value}
                                    key={option.value}
                                    onClick={() => {
                                        if (value === option.value && !simulation) return setIsOpen(false);
                                        if (isTypeahead && !simulation) {
                                            inputRef!.current!.blur();
                                            setIsOpen(false);
                                            setQuery("");
                                        }
                                        onChange({
                                            target: {
                                                name,
                                                value: option.value,
                                                type: "select",
                                            },
                                        });
                                    }}
                                >
                                    {option.label}
                                </Option>
                            );
                        })}
                        {queriedOptions.length === 0 && (
                            <Option
                                onClick={() => {
                                    setIsOpen(false);
                                    setQuery("");
                                }}
                            >
                                <FormattedMessage {...messages.noResults} />
                            </Option>
                        )}
                    </div>
                </Options>
            )}
        </Wrapper>
    );
}

export default InputSelect;
